import { and, or } from 'tio-ui/utilities';
import Chip from 'tio-reimagined/components/chip';
import { concat, hash } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { PhArrowSquareUpRight, PhCompass, PhClock, PhCreditCard } from 'ember-phosphor-icons';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Component from '@glimmer/component';
import safeFormatDate from '../../../helpers/safe-format-date.ts';
import type { IntlService } from 'ember-intl';
import type { TasProgramInstance as TasProgramInstanceResult } from '../../../models/search.ts';
import WhosViewingComponent from '../../tio/whos-viewing.gts';
import type { Appearance } from '../../../services/appearance.ts';
import type FeaturesService from '../../../services/features';

interface ApplicationRowSignature {
  Args: {
    application: TasProgramInstanceResult;
    personRoute: string;
    rootRoute: string;
    viewers?: Appearance[];
    /** Query param to enable new approval UX in production (tio-admin only) */
    newApproval?: boolean;
  };
  Element: HTMLDivElement;
}

const priorityBarColor = (dateString?: string) => {
  if (!dateString) return 'bg-gray-300';

  const date = new Date(dateString);
  const now = new Date();
  const daysAgo = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (daysAgo > 5) return 'bg-red-500';
  if (daysAgo >= 3) return 'bg-yellow-400';
  return 'bg-green-500';
};

export default class ApplicationRow extends Component<ApplicationRowSignature> {
  @service declare intl: IntlService;
  @service declare features: FeaturesService;

  // Convert viewers to array if it's an object
  get appearances(): Appearance[] {
    if (!this.args.viewers) return [];
    return Array.isArray(this.args.viewers) ? this.args.viewers : Object.values(this.args.viewers);
  }

  get isAssetReview(): boolean {
    return this.args.application.recordType === 'asset';
  }

  get isCourseCash(): boolean {
    return (
      this.isAssetReview ||
      (this.args.application.tasApplicationCourseCashReservedAmountInteger ?? 0) > 0 ||
      (this.args.application.tasApplicationCourseCashUsedAmountInteger ?? 0) > 0
    );
  }

  statusText = (state: string): string => {
    if (this.intl.exists(`common.status_message.to.${state}`)) {
      return this.intl.t(`common.status_message.to.${state}`);
    }

    return this.intl.t('common.status_message.to.TAS.ApplicationState.UPDATED_ON');
  };

  get originalSubmissionTs(): string | undefined {
    const application = this.args.application;

    if (this.isAssetReview) {
      return application.attentionPriorityDate;
    }

    return (
      application.tasApplicationAttentionPriorityDate ||
      application.tasProgramInstanceAttentionPriorityDate ||
      application.tasApplicationLatestSubmissionTs ||
      application.tasProgramInstanceLatestSubmissionTs
    );
  }

  /**
   * Determines if new approval button should be shown
   * - Non-production: Enabled with feature flag
   * - Production: Enabled with feature flag AND ?newApproval=true query param (tio-admin only)
   */
  get showNewApprovalButton(): boolean {
    const hasFeatureFlag = this.features.isEnabled('newTasApprovalUx');
    const isProduction = this.features.tioEnvironment === 'production';
    const isAdminApp = this.features.isAdminApp;
    const hasQueryParam = this.args.newApproval === true;

    // In production (admin only), require both feature flag and query param
    if (isProduction && isAdminApp) {
      return hasFeatureFlag && hasQueryParam;
    }

    // In non-production or non-admin apps, just need feature flag
    return hasFeatureFlag;
  }

  <template>
    <div
      class="flex justify-between items-start border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md dark:shadow-black/20 dark:hover:shadow-black/30 transition relative bg-white"
    >
      {{! Left vertical red bar }}
      <div
        class="absolute left-0 top-0 bottom-0 w-1 rounded-l
          {{priorityBarColor
            (or
              @application.tasProgramInstanceAttentionPriorityDate
              @application.attentionPriorityDate
            )
          }}"
      />
      {{! Main Content }}
      <div class="pl-4 w-full">
        <div class="flex justify-between items-center">
          <div>
            <h3 class="font-semibold text-gray-900 flex gap-x-4">
              {{#if this.isAssetReview}}
                <span>{{t "common.tas_approval_queue.assistance_review"}}</span>
                <Chip class="uppercase bg-amber-100 text-amber-800">
                  {{t "common.tas_approval_queue.card_usage_review"}}
                </Chip>
                <PhCreditCard @size={{16}} class="text-amber-800" />
              {{else}}
                <span>{{t "common.tas_approval_queue.application_number" id=@application.id}}</span>
                {{#if @application.tasApplicationIdKeyword}}
                  <Chip class="uppercase">{{t "common.tas_approval_queue.course"}}</Chip>
                {{else}}
                  <Chip class="uppercase">{{t "common.tas_approval_queue.program"}}</Chip>
                {{/if}}
                {{#if this.isCourseCash}}
                  <PhCreditCard @size={{16}} class="text-amber-800" />
                {{/if}}
              {{/if}}
            </h3>
            <p class="text-sm text-gray-500">
              <LinkTo @route={{@personRoute}} @model={{@application.personId}}>
                <div class="flex items-center flex-nowrap">
                  {{@application.employeeNameKeyword}}
                  ({{@application.employeePayrollIdKeyword}}) &bull;
                  {{@application.companyName}}
                  <PhArrowSquareUpRight @size="16" />
                </div>
              </LinkTo>
            </p>
          </div>

          <div class="flex items-center gap-2">
            {{#if this.appearances.length}}
              <WhosViewingComponent @appearances={{this.appearances}} />
            {{/if}}
            {{#unless this.isAssetReview}}
              <LinkTo
                @route={{if
                  @application.tasApplicationIdKeyword
                  (concat @rootRoute ".tuition-assistance.applications.show")
                  (concat @rootRoute ".tuition-assistance.program-instances.show")
                }}
                @model={{if
                  @application.tasApplicationIdKeyword
                  @application.tasApplicationIdKeyword
                  @application.tasProgramInstanceIdKeyword
                }}
                class="inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md border border-blue-600 text-blue-600 hover:bg-blue-50 dark:border-blue-500 dark:text-blue-400 dark:hover:bg-blue-950/50 transition-colors"
                data-test-view-application-button
              >
                {{t "common.tas_approval_queue.view"}}
              </LinkTo>
            {{/unless}}

            {{#if (and @application.tasApplicationIdKeyword this.showNewApprovalButton)}}
              <LinkTo
                @route={{concat @rootRoute ".tas.applications.approval"}}
                @model={{@application.tasApplicationIdKeyword}}
                @query={{if this.isAssetReview (hash courseCash=true) (hash)}}
                class="inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md border border-blue-600 text-blue-600 hover:bg-blue-50 dark:border-blue-500 dark:text-blue-400 dark:hover:bg-blue-950/50 transition-colors"
                data-test-new-approval-view-button
              >
                {{t "common.tas_approval_queue.new_view"}}
              </LinkTo>
            {{/if}}
          </div>
        </div>

        <div class="mt-3 grid grid-cols-4 gap-y-1 gap-x-4 text-sm text-gray-600">
          <div class="flex items-center gap-1 col-span-2">
            <PhCompass @size={{16}} class="text-gray-400" />
            <span class="font-medium">{{t "common.status"}}</span>
            {{#if this.isAssetReview}}
              {{t "common.tas_approval_queue.pending_assistance_review"}}
            {{else if @application.tasApplicationStateKeyword}}
              {{this.statusText @application.tasApplicationStateKeyword}}
            {{else if @application.tasProgramInstanceStateKeyword}}
              {{this.statusText @application.tasProgramInstanceStateKeyword}}
            {{/if}}
          </div>
          <div class="flex items-center gap-1">
            <PhClock class="h-4 w-4 text-gray-400" />
            <span class="font-medium">{{t "common.tas_approval_queue.last_action"}}</span>
            {{safeFormatDate
              (or
                @application.tasApplicationLatestStateChangeAtDate
                @application.tasProgramInstanceLatestStateChangeAtDate
                @application.tasAssetUpdatedAtDate
              )
              month="short"
              day="2-digit"
              year="numeric"
            }}
          </div>

          <div class="flex justify-end items-center gap-1">
            {{#if this.originalSubmissionTs}}
              <span
                class="font-medium"
                title={{safeFormatDate
                  this.originalSubmissionTs
                  month="short"
                  day="2-digit"
                  year="numeric"
                }}
              >
                {{t
                  "common.tas_approval_queue.submitted"
                  time=(safeFormatDate
                    this.originalSubmissionTs month="short" day="2-digit" year="numeric"
                  )
                }}
              </span>
            {{else}}
              <span class="font-medium">{{t "common.tas_approval_queue.submitted" time="—"}}</span>
            {{/if}}
          </div>
        </div>
      </div>
    </div>
  </template>
}
